/**
 * Centralized label mappings for all ITSM modules
 * Converts internal enum values to user-friendly display labels
 */

// Common Impact/Urgency/Priority labels
export const impactLabels: Record<string, string> = {
  'high_1': '1 - High',
  'medium_2': '2 - Medium',
  'low_3': '3 - Low'
};

export const urgencyLabels: Record<string, string> = {
  'high_1': '1 - High',
  'medium_2': '2 - Medium',
  'low_3': '3 - Low'
};

export const priorityLabels: Record<string, string> = {
  // Matrix-based priority format (matches backend enums)
  'critical_1': 'Critical',
  'high_2': 'High',
  'moderate_3': 'Moderate',
  'low_4': 'Low',
  'planning_5': 'Planning',
  // Legacy formats for backward compatibility
  'CRITICAL': 'Critical',
  'HIGH': 'High',
  'NORMAL': 'Moderate',
  'MEDIUM': 'Moderate',
  'LOW': 'Low',
  // Numeric values for backward compatibility
  '1': 'Critical',
  '2': 'High',
  '3': 'Moderate',
  '4': 'Low',
  '5': 'Planning'
};

// Incident State labels
export const incidentStateLabels: Record<string, string> = {
  'new': 'New',
  'in_progress': 'In Progress',
  'on_hold': 'On Hold',
  'resolved': 'Resolved',
  'closed': 'Closed',
  'cancelled': 'Cancelled'
};

// Problem State labels
export const problemStateLabels: Record<string, string> = {
  'new': 'New',
  'assess_1': 'Assess 1',
  'assess_2': 'Assess 2',
  'root_cause_analysis': 'Root Cause Analysis',
  'resolved': 'Resolved',
  'closed': 'Closed',
  'cancelled': 'Cancelled'
};

// Change State labels
export const changeStateLabels: Record<string, string> = {
  'new': 'New',
  'assess': 'Assess',
  'authorize': 'Authorize',
  'scheduled': 'Scheduled',
  'implement': 'Implement',
  'review': 'Review',
  'closed': 'Closed',
  'cancelled': 'Cancelled'
};

// Case State labels
export const caseStateLabels: Record<string, string> = {
  'new': 'New',
  'open': 'Open',
  'awaiting_info': 'Awaiting Info',
  'on_hold': 'On Hold',
  'solution_proposed': 'Solution Proposed',
  'in_progress': 'In Progress',
  'work_in_progress': 'Work in Progress',
  'pending_customer': 'Pending Customer',
  'pending_vendor': 'Pending Vendor',
  'pending_approval': 'Pending Approval',
  'approved': 'Approved',
  'resolved': 'Resolved',
  'closed': 'Closed',
  'closed_complete': 'Closed Complete',
  'closed_incomplete': 'Closed Incomplete',
  'cancelled': 'Cancelled',
  'skipped': 'Skipped',
  'auto_resolved': 'Auto Resolved'
};

// Alert State labels
export const alertStateLabels: Record<string, string> = {
  'new': 'New',
  'open': 'Open',
  'acknowledged': 'Acknowledged',
  'assigned': 'Assigned',
  'in_progress': 'In Progress',
  'resolved': 'Resolved',
  'auto_resolved': 'Auto Resolved',
  'closed': 'Closed',
  'suppressed': 'Suppressed'
};

// Change Type labels
export const changeTypeLabels: Record<string, string> = {
  'normal': 'Normal',
  'standard': 'Standard',
  'emergency': 'Emergency',
  'infrastructure': 'Infrastructure'
};

// Change Risk labels
export const changeRiskLabels: Record<string, string> = {
  'high': 'High',
  'moderate': 'Moderate',
  'low': 'Low'
};

// Case Category labels
export const caseCategoryLabels: Record<string, string> = {
  'question': 'Question',
  'issue': 'Issue',
  'request': 'Request',
  'change': 'Change'
};

// Problem Task State labels
export const problemTaskStateLabels: Record<string, string> = {
  'new': 'New',
  'in_progress': 'In Progress',
  'closed': 'Closed'
};

// Problem Task Type labels
export const problemTaskTypeLabels: Record<string, string> = {
  'root_cause_analysis': 'Root Cause Analysis',
  'general': 'General'
};

// Problem Task Assigned To labels
export const problemTaskAssignedToLabels: Record<string, string> = {
  'customer': 'Customer',
  'infogain': 'Infogain',
  'vendor': 'Vendor'
};

/**
 * Generic helper to get display label for any enum value
 * Falls back to the raw value if no mapping exists
 */
export const getDisplayLabel = (value: string | null | undefined, labelMap: Record<string, string>): string => {
  if (!value) return '-';
  return labelMap[value] || value;
};

/**
 * Format priority with number prefix
 * Example: "critical_1" -> "1 - Critical", "high_2" -> "2 - High"
 */
export const formatPriorityLabel = (priority: string | null | undefined): string => {
  if (!priority) return '-';
  const match = priority.match(/_(\d)/);
  const number = match ? match[1] : '';
  const label = priorityLabels[priority];

  // If we have both number and label, format as "N - Label"
  if (label && number) {
    return `${number} - ${label}`;
  }

  // Otherwise return label if available, or original value
  return label || priority;
};
